import { useState } from 'react';
import { CheckCircle, Loader2, Upload, X } from 'lucide-react';
import { useApplyForJob } from '../../hooks/useJobBoard';

interface Props {
  jobId: string;
  onClose?: () => void;
  colors?: {
    buttonColor?: string;
  };
  candidateDisclaimer?: string;
  privacyPolicyUrl?: string;
}
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default function JobApplicationForm({ jobId, onClose, colors, candidateDisclaimer, privacyPolicyUrl }: Props) {
  const apply = useApplyForJob();
  const [values, setValues] = useState({ firstName: '', lastName: '', emailAddress: '', contactNumber: '', coverLetter: '' });
  const [resume, setResume] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const setValue = (field: keyof typeof values, value: string) => setValues((current) => ({ ...current, [field]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!values.firstName.trim()) nextErrors.firstName = 'First name is required';
    if (!values.lastName.trim()) nextErrors.lastName = 'Last name is required';
    if (!values.emailAddress.trim()) nextErrors.emailAddress = 'Email address is required';
    if (!resume) nextErrors.resume = 'Resume is required';
    else if (!['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(resume.type) && !/\.(pdf|doc|docx)$/i.test(resume.name)) nextErrors.resume = 'Use a PDF, DOC, or DOCX file';
    else if (resume.size > MAX_FILE_SIZE) nextErrors.resume = 'Resume must be 10 MB or smaller';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    const form = new FormData();
    form.append('JobId', jobId); form.append('FirstName', values.firstName); form.append('LastName', values.lastName); form.append('EmailAddress', values.emailAddress); form.append('ContactNumber', values.contactNumber); form.append('CoverLetter', values.coverLetter); form.append('Resume', resume as File);
    apply.mutate(form, { onSuccess: () => setSubmitted(true) });
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-8 text-center">
        <CheckCircle
          className="mx-auto text-emerald-600"
          size={44}
        />

        <h2 className="mt-4 text-xl font-bold text-slate-900">
          Application Submitted
        </h2>

        <p className="mt-2 text-sm text-slate-600">
          Thank you for your interest. The hiring team will
          review your application and contact you if your
          qualifications match the position.
        </p>

        {onClose && (
          <button
            onClick={onClose}
            style={{
              backgroundColor: colors?.buttonColor,
            }}
            className="mt-6 rounded-xl px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  const inputClass = "input-base";

  return (
    <form
      onSubmit={submit}
      className="relative space-y-5 text-left"
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-0 top-0 rounded-lg p-2 text-slate-400 transition hover:bg-slate-100"
        >
          <X size={18} />
        </button>
      )}

      <div className="mb-6 border-b border-slate-100 pb-4">
        <h3 className="text-xl font-bold text-slate-900">
          Application Details
        </h3>

        <p className="mt-1 text-sm text-slate-500">
          Complete the form below and upload your resume.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {(["firstName", "lastName"] as const).map(
          (field) => (
            <div key={field}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                {field === "firstName"
                  ? "First Name"
                  : "Last Name"}{" "}
                *
              </label>

              <input
                className={inputClass}
                value={values[field]}
                onChange={(e) =>
                  setValue(field, e.target.value)
                }
              />

              {errors[field] && (
                <p className="mt-1 text-xs text-red-600">
                  {errors[field]}
                </p>
              )}
            </div>
          )
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold text-slate-700">
          Email Address *
        </label>

        <input
          type="email"
          className={inputClass}
          value={values.emailAddress}
          onChange={(e) =>
            setValue("emailAddress", e.target.value)
          }
        />

        {errors.emailAddress && (
          <p className="mt-1 text-xs text-red-600">
            {errors.emailAddress}
          </p>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold text-slate-700">
          Contact Number
        </label>

        <input
          type="tel"
          className={inputClass}
          value={values.contactNumber}
          onChange={(e) =>
            setValue("contactNumber", e.target.value)
          }
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold text-slate-700">
          Cover Letter
        </label>

        <textarea
          rows={4}
          className={inputClass}
          value={values.coverLetter}
          onChange={(e) =>
            setValue("coverLetter", e.target.value)
          }
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold text-slate-700">
          Resume *
          <span className="ml-1 font-normal text-slate-400">
            PDF, DOC, DOCX, up to 10 MB
          </span>
        </label>

        <label
          className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed bg-slate-50 px-4 py-4 text-sm text-slate-600 transition"
          style={{
            borderColor:
              colors?.buttonColor ?? "#cbd5e1",
          }}
        >
          <Upload size={18} />

          <span className="flex-1 truncate">
            {resume?.name ??
              "Choose a resume file"}
          </span>

          {resume && (
            <span
              className="rounded-full px-2 py-1 text-xs font-semibold"
              style={{
                backgroundColor: `${colors?.buttonColor ?? "#172554"}20`,
                color: colors?.buttonColor,
              }}
            >
              Selected
            </span>
          )}

          <input
            type="file"
            className="sr-only"
            accept=".pdf,.doc,.docx"
            onChange={(e) =>
              setResume(
                e.target.files?.[0] ?? null
              )
            }
          />
        </label>

        {errors.resume && (
          <p className="mt-1 text-xs text-red-600">
            {errors.resume}
          </p>
        )}
      </div>
      {candidateDisclaimer && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-5 text-slate-600">
          <p className="mb-2 font-semibold text-slate-700">
            Application Disclaimer
          </p>

          <p className="whitespace-pre-wrap">
            {candidateDisclaimer}
          </p>

          {privacyPolicyUrl && (
            <p className="mt-3">
              For more information about how your personal information is handled, please review the{" "}
              <a
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 underline"
              >
                Privacy Policy
              </a>
              .
            </p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={apply.isPending}
        style={{
          backgroundColor: colors?.buttonColor,
        }}
        className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
      >
        {apply.isPending && (
          <Loader2
            size={18}
            className="animate-spin"
          />
        )}

        Submit Application
      </button>
    </form>
  );
}